/* eslint-disable no-param-reassign */
import { useEffect, useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { FinancialStatementPeriod } from 'common/types/financials';
import { CellChange } from 'components/Spreadsheet/Spreadsheet.types';
import { useFinancials } from 'context/FinancialsContext';
import { IWorkbook } from 'models/interfaces/spreadsheet-types/workbook-types';
import logger from 'utilities/logger';
import { SecurityModel } from '../../../../../api';
import { useStore } from '../../../../../common/store';
import { UseStoreValues } from '../../../../../common/types/store';
import { HandlePushingAttributeMappingsPayload } from '../../../../../components/Drawer/attribute-mapped-widget-drawer/attribute-mapped-widget-drawer.types';
import { AttributeMappedWidgetDrawer } from '../../../../../components/Drawer/attribute-mapped-widget-drawer/AttributeMappedWidgetDrawer';
import { CitationPushedPayload } from '../../../../../components/Drawer/attribute-mapped-widget-drawer/AttributeMappedWidgetDrawerContext';
import WidgetDrawerProps from '../../../../../components/Drawer/widget-drawer/WidgetDrawer.props';
import { EventType, useEventBus, useEventSubscription } from '../../../../../context';
import { DocumentReference, useDocumentFootnotes } from '../../../../../context/DocumentFootnotesContext';
import { CellValue } from '../../../../../models/interfaces/spreadsheet-types/core-types';
import { CapTableReferenceGenerator, MappingReferenceData } from '../../../../../services/reference-generator';
import { CitationReferenceData } from '../../../../../services/reference-generator/types';
import { ClearAttributeMappingsPayload } from '../../../../../services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { useCellMappingStore } from '../../../../../store';
import { useAttributeMapperStore } from '../../../../../store/useAttributeMapperStore';
import { removeClassName } from '../../../../../utilities';
import { AttributeMappingType } from '../../../../Financials/financials-widget-drawer/types';
import { rowConfig } from '../../data';
import { CapTableCellWithReferences } from '../captable-table/CaptableTable.props';

export interface CapTableWidgetDrawerProps extends WidgetDrawerProps {
  workbook: IWorkbook;
  onTableChange: (cell: any, value: number) => void;
  captableSetProgrammaticUpdate?: (isProgrammatic: boolean) => void;
}

const CapTableWidgetDrawer = ({
  onTableChange,
  workbook,
  spreadsheetNotesProps,
  notesProps,
  documentReferencesProps,
  applicationContext,
  documentApplicationStorageKey,
  captableSetProgrammaticUpdate,
}: CapTableWidgetDrawerProps) => {
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;
  const { getCellMapping } = useCellMappingStore();
  const { attributeMapperManager, getAttributeMapperManager, clearAttributeMapperManager } = useAttributeMapperStore();
  const { registerFootnote, clearFootnotes } = useDocumentFootnotes();

  const { publish } = useEventBus();
  const [citation, setCitation] = useState<(CitationPushedPayload & { documentReferenceId: number }) | undefined>();

  useEventSubscription(
    EventType.SPREADSHEET_ACTIVE_CELL_CHANGED,
    ([newCell]: [{ cellInfo: CapTableCellWithReferences }]) => {
      if (citation) return;
      const refs = newCell.cellInfo.mappingReferences;
      if (refs && refs.length > 0) {
        const first = refs[0];
        publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, {
          documentReferenceId: first.document_reference,
          sheetName: first.sheet_name,
        });
      }
    }
  );

  // this might move down into the md viewer
  useEventSubscription(
    EventType.SPREADSHEET_ACTIVE_CELL_CHANGED,
    ([newCell, _prevCell]: [{ cellInfo: CapTableCellWithReferences }, CapTableCellWithReferences]) => {
      if (!citation) return;

      const existingRefIndex = newCell.cellInfo.citationReferences?.findIndex(
        item => item.field_name === newCell.cellInfo.alias && item.document_reference === citation.documentReferenceId
      );

      if (existingRefIndex !== undefined && existingRefIndex !== -1 && newCell.cellInfo.citationReferences) {
        // Add to existing citation's token references
        newCell.cellInfo.citationReferences[existingRefIndex].tokenReferences.push({
          token_ids: citation.tokenIds,
          page: citation.page,
          plain_text: citation.selectedText,
          notes: [],
        });
      } else {
        const createdCitation: CitationReferenceData = {
          document_reference: citation.documentReferenceId,
          field_name: newCell.cellInfo.alias ?? '',
          tokenReferences: [
            {
              token_ids: citation.tokenIds,
              page: citation.page,
              plain_text: citation.selectedText,
              notes: [],
            },
          ],
        };

        newCell.cellInfo.citationReferences = newCell.cellInfo.citationReferences
          ? [...newCell.cellInfo.citationReferences, createdCitation]
          : [createdCitation];
      }

      onTableChange(newCell.cellInfo, newCell.cellInfo.value as number);
      setCitation(undefined);

      enqueueSnackbar(`Citation added to ${newCell.cellInfo.alias}(${newCell.cellInfo.key})`, { variant: 'success' });

      registerCapTableCitationFootnotes();
    }
  );

  const captableIndex = (alias: string) =>
    rowConfig({
      isDisabled: false,
      measurementDate: '',
      customTitles: {},
      firmName: '',
    }).findIndex(item => item.alias === alias);

  // Helper function to collect and register citation footnotes from cap table cells
  const registerCapTableCitationFootnotes = () => {
    if (!workbook?.captable?.cells) return;

    const cellsWithCitations = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).filter(
      cell => cell.citationReferences && cell.citationReferences.length > 0
    );

    if (cellsWithCitations.length === 0) {
      clearFootnotes();
      return;
    }

    // Group citations by cell to create unique footnote IDs
    cellsWithCitations.forEach(cell => {
      const cellKey = cell.key || `${cell.columnLegend}${cell.rowNumber}`;

      if (!cell.citationReferences) return;

      // Convert CitationReferenceData to DocumentReference format
      const documentReferencesMap = new Map<number, DocumentReference>();

      cell.citationReferences.forEach(citationRef => {
        const docId = citationRef.document_reference;

        if (!documentReferencesMap.has(docId)) {
          documentReferencesMap.set(docId, {
            documentId: docId,
            fields: [],
          });
        }

        const docRef = documentReferencesMap.get(docId)!;

        // For citations, we use the field_name and create pseudo cell references from token positions
        const existingFieldIndex = docRef.fields.findIndex(f => f.fieldName === citationRef.field_name);

        const cellReferences = citationRef.tokenReferences.map(tokenRef => ({
          ref: `p${tokenRef.page}-${tokenRef.token_ids.join(',')}`,
          direction: 'positive' as const,
        }));

        if (existingFieldIndex >= 0) {
          // Merge cell references into existing field
          docRef.fields[existingFieldIndex].cellReferences.push(...cellReferences);
        } else {
          // Add new field entry
          docRef.fields.push({
            fieldName: citationRef.field_name,
            sheetName: 'Citation', // Citations don't have sheet names, use a placeholder
            cellReferences,
          });
        }
      });

      // Register footnote for this cell
      registerFootnote(`captable-cell-${cellKey}`, Array.from(documentReferencesMap.values()));
    });
  };

  // Update footnotes whenever citations in cells change
  useEffect(() => {
    registerCapTableCitationFootnotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbook?.captable?.cells]);

  // Cleanup footnotes on unmount
  useEffect(
    () => () => {
      clearFootnotes();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const clearCellReferences = (docRefPayload?: ClearAttributeMappingsPayload) => {
    const cellList = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).filter(item => {
      const hasMappedRefs = item.mappingReferences?.length;
      const hasColumnarRefs = item.columnarReferences?.length;
      const hasColRefsMetadata = item.columnarReferencesMetadata?.length;
      const hasCitationRefs = item.citationReferences?.length;

      return hasMappedRefs || hasColumnarRefs || hasColRefsMetadata || hasCitationRefs;
    });

    const cellUpdates: { cell: CapTableCellWithReferences; value: CellValue }[] = [];
    cellList.forEach(cell => {
      // If no docRefPayload, clear all references
      if (!docRefPayload) {
        cell.mappingReferences = [];
        cell.columnarReferences = [];
        cell.columnarReferencesMetadata = [];
        cell.className = removeClassName(cell.className ?? '', 'cell-with-references');
        cell.valueViewer = null;

        cellUpdates.push({
          cell,
          value: cell.value,
        });
        return;
      }

      // Otherwise, filter references for specific document/sheet
      const { documentReferenceId, sheet } = docRefPayload;
      let hasChanges = false;

      // Filter mappingReferences
      if (cell.mappingReferences && cell.mappingReferences.length > 0) {
        const originalLength = cell.mappingReferences.length;
        cell.mappingReferences = cell.mappingReferences.filter(ref => {
          const matchesDoc = ref.document_reference === documentReferenceId;
          const matchesSheet = !sheet || ref.sheet_name === sheet;
          return !(matchesDoc && matchesSheet);
        });
        hasChanges = hasChanges || originalLength !== cell.mappingReferences.length;
      }


      const hasAnyReferences =
        (cell.mappingReferences && cell.mappingReferences.length > 0)

      if (hasChanges && !hasAnyReferences) {
        cell.mappingReferences = [];
        cell.columnarReferences = [];
        cell.columnarReferencesMetadata = [];
        cell.className = removeClassName(cell.className ?? '', 'cell-with-references');
        cell.valueViewer = null;
      }

      if (hasChanges) {
        cellUpdates.push({
          cell,
          value: cell.value,
        });
      }
    });

    cellUpdates.forEach(change => {
      onTableChange(change.cell, change.value as number);
    });

    // Recalculate title cells for affected columns
    if (docRefPayload && cellUpdates.length > 0) {
      const referenceGeneratorService = new CapTableReferenceGenerator();
      const affectedColumns = new Set<string>();

      cellUpdates.forEach(({ cell }) => {
        const { columnLegend } = cell as CapTableCellWithReferences;
        if (columnLegend) {
          affectedColumns.add(columnLegend);
        }
      });

      affectedColumns.forEach(columnLegend => {
        const capCell = workbook.captable.cells[`${columnLegend}1`] as CapTableCellWithReferences;
        if (capCell) {
          const columnMappingReferences: MappingReferenceData[] = [];
          Object.keys(workbook.captable.cells).forEach(key => {
            if (key.startsWith(columnLegend) && key !== `${columnLegend}1`) {
              const cell = workbook.captable.cells[key] as CapTableCellWithReferences;
              if (cell.mappingReferences && cell.mappingReferences.length > 0) {
                columnMappingReferences.push(...cell.mappingReferences);
              }
            }
          });
          const docRefsData = referenceGeneratorService.getDocumentReferences(columnMappingReferences);
          capCell.columnarReferences = docRefsData.documentIds;
          capCell.columnarReferencesMetadata = docRefsData.metadata;
          capCell.dispatchEvent();
        }
      });
    }
  };

  const pushAttributeMappingsToCapTable = async (payload?: HandlePushingAttributeMappingsPayload) => {
    const mapper = getAttributeMapperManager();
    if (!mapper) {
      return;
    }

    // Set flag to indicate programmatic updates are happening
    captableSetProgrammaticUpdate?.(true);

    try {
      const securityFields = rowConfig({ isDisabled: false, measurementDate: '', customTitles: {} }).map(
        item => item.alias
      );
      const mappingService = new CapTableReferenceGenerator(securityFields);

      // find which cell is in an expr state
      const cellsInExprState = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).filter(
        cell => cell.expr === '='
      );

      if (!cellsInExprState.length) {
        return;
      }

      if (!payload?.documentReferencePayload) {
        return;
      }

      cellsInExprState.forEach(cell => {
        const titleCellForColumn = workbook.captable.cells[`${cell.columnLegend}1`] as CapTableCellWithReferences;

        // need to clear similar xValue and yValue pairs from the mapper
        const expectedXValue = cell.alias;
        const expectedYValue = titleCellForColumn.value;

        // for cap table each single cell selected in the table must only be represented by one value below
        mapper.removeSingleCellByXYValue({
          xValue: expectedXValue,
          yValue: expectedYValue,
        });

        mapper.handleSingularAddition(payload.documentReferencePayload!, {
          // coordinates should be the cell in the drawer sheet
          coordinates: `${payload.selectedCell?.cellInfo.key}`,

          // these are the source values
          xValue: cell.alias,
          yValue: titleCellForColumn.value,
          direction: 'positive',
        });
      });

      const mapping = getCellMapping();
      const evaluatedAttributeData = mapper.calculateValues(mapping);

      // Get the sheet attribute mapping which contains the reference data
      const { sheetAttributeMapping } = mapper;

      // Generate mapping references from the sheet attribute data (not evaluated data)
      const generatedReferences = mappingService.generateMappingReferences(sheetAttributeMapping.data);

      const captableChangeSet: CellChange[] = [];

      // clearCellReferences();

      Object.keys(evaluatedAttributeData).forEach(columnarMetric => {
        Object.keys(evaluatedAttributeData[columnarMetric]).forEach(rowMetric => {
          const captableRowIndex = captableIndex(rowMetric);

          if (captableRowIndex >= 0) {
            const updatedCell = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).find(
              (item: CapTableCellWithReferences) => {
                // Only consider cells that are in mapping mode (expr === '=')
                if (item.expr !== '=') return false;

                const titleColumn = workbook.captable.cells[`${item.columnLegend}1`];
                if (!titleColumn) return false;

                return item.alias === rowMetric && titleColumn.value === columnarMetric;
              }
            ) as CapTableCellWithReferences | undefined;
            if (updatedCell) {
              const mappedValue = evaluatedAttributeData[columnarMetric][rowMetric].value;

              // // Attach reference metadata to the cell
              const referenceMetadata = mappingService.getReferenceMetadataForCell(
                columnarMetric,
                rowMetric,
                generatedReferences
              );

              // // Add mapping reference metadata to cell
              const titleCell = workbook.captable.cells[`${updatedCell.columnLegend}1`];

              if (referenceMetadata) {
                updatedCell.mappingReferences = referenceMetadata;
                const columnarRefs = mappingService.getDocumentReferences(referenceMetadata);

                // set col refs on column
                titleCell.columnarReferences = columnarRefs.documentIds;
                titleCell.columnarReferencesMetadata = columnarRefs.metadata;

                captableChangeSet.push({
                  cell: titleCell,
                  value: titleCell.value,
                });
              } else {
                updatedCell.mappingReferences = [];
                titleCell.columnarReferences = [];
                titleCell.columnarReferencesMetadata = [];
              }

              // here add in the cell refs for the col

              captableChangeSet.push({
                cell: updatedCell,
                value: mappedValue,
              });
            }
          }
        });
      });

      const fullChanges = captableChangeSet;
      fullChanges.forEach(change => {
        onTableChange(change.cell, change.value);
      });

      if (payload?.showNotification) {
        enqueueSnackbar('Mapping pushed to captable', { variant: 'success' });
      }
    } finally {
      // Reset flag after all changes are complete
      captableSetProgrammaticUpdate?.(false);
    }
  };

  const handleClearMappings = (docRefPayload?: ClearAttributeMappingsPayload) => {
    clearCellReferences(docRefPayload);
    clearAttributeMapperManager(docRefPayload);
    pushAttributeMappingsToCapTable(false);

    registerCapTableCitationFootnotes();
  };

  const filterRows = row => !row.readOnly;
  const mapRowsToOptions = row => ({ label: row.value, value: row.alias });

  const attributeReferenceConfig = useMemo(() => {
    if (!captableInfo) return { columns: [], rows: [], selectionMode: AttributeMappingType.SINGLE_CELL };

    const columns = captableInfo.securities?.map((security: SecurityModel) => ({
      label: security.name,
      value: security.id,
    }));

    const captableRows = rowConfig({
      isDisabled: false,
      measurementDate: '',
      customTitles: {},
      firmName: '',
    });
    const captableOptions = captableRows.filter(filterRows).map(mapRowsToOptions);
    const rows = [{ label: 'CapTable', value: null, isTitle: true }, ...captableOptions];
    return {
      columns,
      rows,
      selectionMode: AttributeMappingType.SINGLE_CELL,
    };
  }, [captableInfo]);

  const handleCitationPushed = (payload: CitationPushedPayload, documentReferenceId: number) => {
    setCitation({ ...payload, documentReferenceId });
  };

  return (
    <AttributeMappedWidgetDrawer
      documentApplicationStorageKey={documentApplicationStorageKey}
      attributeMapper={attributeMapperManager}
      attributeReferenceConfig={attributeReferenceConfig}
      onCitationPushed={handleCitationPushed}
      applicationContext={applicationContext}
      documentReferencesProps={documentReferencesProps}
      notesProps={notesProps}
      spreadsheetNotesProps={spreadsheetNotesProps}
      handlePushingAttributeMappings={pushAttributeMappingsToCapTable}
      handleClearMappings={handleClearMappings}
    />
  );
};

export default CapTableWidgetDrawer;
